import { s3URL } from '@mathflat/shared/@common/utils/s3'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'

import { useEnglishflatConnect } from '@/hooks/useEnglishflatConnect'
import commonRepo from '@/repository/common.repo'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import S, { WM_RATIO } from './EarlybirdBanner.style'

const tileUrl = s3URL.mathflatAssetsBucket.teacher('images/gnb/earlybird/tile.png')
const logoUrl = s3URL.mathflatAssetsBucket.teacher('images/gnb/earlybird/logo-englishflat.png')

// 1280 을 경계로 full/compact 를 가른다. 자리가 모자라서가 아니라(1280 가용 403px),
// 화면이 좁아질수록 GNB 가 빽빽해지므로 손님인 배너부터 줄이는 쪽이 맞다고 정한 구간.
const FULL_VARIANT_QUERY = '(min-width: 1280px)'

// 롤링 문구 — 좁아지면 앞머리를 덜어낸다. 얼리버드 랜딩 띠배너 카피와 같은 말.
const HEAD = {
  full: { lead: '이제 ', hot: '영어', tail: '도 매쓰플랫처럼' },
  compact: { lead: '', hot: '영어', tail: '도 매쓰플랫처럼' },
} as const
const OFFER = {
  full: { lead: '지금 신청하면 모두 ', hot: '0원', tail: '!' },
  compact: { lead: '지금 신청하면 ', hot: '0원', tail: '!' },
} as const

const LOGO_HEIGHT = 16

const EarlybirdBanner = observer(() => {
  const handleEnglishflatConnect = useEnglishflatConnect()
  const [isFullVariant, setIsFullVariant] = useState(
    () => window.matchMedia(FULL_VARIANT_QUERY).matches,
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia(FULL_VARIANT_QUERY)
    const handleChange = (event: MediaQueryListEvent) => setIsFullVariant(event.matches)
    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // 이미 쓰는 학원에는 배너를 걸지 않는다 — 신청을 권하는 자리라 구독 뒤에는 할 말이 없다.
  // 구독은 학원 단위, 과목 권한은 선생님 단위라 둘 다 봐야 드롭다운 배지와 어긋나지 않는다.
  //
  // 신청 직후 재조회가 끝나면 새로고침 없이 바로 사라진다. 기획 흐름 도식은 "새로고침 시
  // 배너 미노출" 로 그려 신청 후에도 남아 있다가 중복 모달을 받는 갈래를 두었지만, 그건
  // 재조회가 없다는 전제에서 나온 것이라 따르지 않기로 했다(기획 확인). 여기서 구독 여부를
  // 한 번만 읽어 얼리면 부팅 중 학원 정보가 늦게 들어올 때 미구독으로 굳어 이미 쓰는 학원에도
  // 배너가 계속 뜬다. 중복 모달은 재조회 실패·다른 탭에서 신청한 경우를 받는다.
  const isEnglishflatSubscribed = Boolean(
    commonRepo.academy?.isEnglishflatAllowed && commonRepo.user?.isEnglishflatAllowed,
  )
  const isVisible =
    commonRepo.permission.byFeature.isAllowed('CONTENT_ENGLISHFLAT') &&
    !commonRepo.isSchoolflat &&
    !commonRepo.isSeoltab &&
    !isEnglishflatSubscribed
  if (!isVisible) return null

  const variant = isFullVariant ? 'full' : 'compact'
  const head = HEAD[variant]
  const offer = OFFER[variant]

  const handleClick = () => {
    mixpanelClient.track('GNB_얼리버드배너_click')
    void handleEnglishflatConnect()
  }

  // 세 칸 + 첫 칸 복제(되감김이 안 보이게).
  const headLine = (
    <span className="gb-line">
      <span className="gb-tile">
        <img src={tileUrl} alt="" />
      </span>
      <span className="gb-text">
        {head.lead}
        <em className="gb-hot">{head.hot}</em>
        {head.tail}
      </span>
    </span>
  )

  return (
    <button
      type="button"
      css={S.banner}
      data-variant={variant}
      aria-label="이제 영어도 매쓰플랫처럼, 지금 신청하면 모두 0원 — 잉글리시플랫 얼리버드 신청"
      onClick={handleClick}
    >
      <span className="gb-roll" aria-hidden>
        <span className="gb-roll__list">
          {headLine}
          <span className="gb-line gb-offer">
            <span className="gb-text">
              {offer.lead}
              <em className="gb-hot">{offer.hot}</em>
              {offer.tail}
            </span>
          </span>
          <span className="gb-line">
            <span
              className="gb-logo"
              style={{ width: LOGO_HEIGHT * WM_RATIO, height: LOGO_HEIGHT }}
            >
              <span
                className="gb-logo__ink"
                style={{
                  maskImage: `url(${logoUrl})`,
                  WebkitMaskImage: `url(${logoUrl})`,
                  maskSize: `${LOGO_HEIGHT * WM_RATIO}px auto`,
                  WebkitMaskSize: `${LOGO_HEIGHT * WM_RATIO}px auto`,
                }}
              />
            </span>
          </span>
          {headLine}
        </span>
      </span>
    </button>
  )
})

export default EarlybirdBanner
